import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Cake,
  Clock,
  AlertCircle,
  Star,
  Sparkles,
  RotateCcw,
  Loader2,
  Download,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatCurrency, formatRelative } from '@/lib/utils'
import type {
  APIResponse,
  CustomerSegmentDetail,
  CustomerSegmentSummary,
  SegmentKey,
} from '@/types'

// Per-segment colour scheme. `tone` paints the card itself (border +
// subtle background tint) so each segment gets its own identity at-a-
// glance — pink for birthdays, rose for lapsed, etc. `iconTone` paints
// the small icon tile in the card header in a matching but stronger
// colour. Order in this map doesn't matter — section grouping below
// controls layout.
const SEGMENT_PRESENTATION: Record<
  SegmentKey,
  { icon: LucideIcon; tone: string; iconTone: string }
> = {
  birthdays_this_month: {
    icon: Cake,
    tone: 'border-pink-200 bg-pink-50/40 dark:border-pink-900 dark:bg-pink-950/30',
    iconTone: 'text-pink-600 bg-pink-100 dark:text-pink-300 dark:bg-pink-950',
  },
  lapsed_60d: {
    icon: RotateCcw,
    tone: 'border-rose-200 bg-rose-50/40 dark:border-rose-900 dark:bg-rose-950/30',
    iconTone: 'text-rose-600 bg-rose-100 dark:text-rose-300 dark:bg-rose-950',
  },
  at_risk: {
    icon: AlertCircle,
    tone: 'border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/30',
    iconTone: 'text-amber-600 bg-amber-100 dark:text-amber-300 dark:bg-amber-950',
  },
  vips: {
    icon: Star,
    tone: 'border-purple-200 bg-purple-50/40 dark:border-purple-900 dark:bg-purple-950/30',
    iconTone: 'text-purple-600 bg-purple-100 dark:text-purple-300 dark:bg-purple-950',
  },
  first_timers_recent: {
    icon: Sparkles,
    tone: 'border-blue-200 bg-blue-50/40 dark:border-blue-900 dark:bg-blue-950/30',
    iconTone: 'text-blue-600 bg-blue-100 dark:text-blue-300 dark:bg-blue-950',
  },
  regulars: {
    icon: Clock,
    tone: 'border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/30',
    iconTone: 'text-emerald-600 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950',
  },
}

const FALLBACK_PRESENTATION = SEGMENT_PRESENTATION.regulars

// Lifecycle section grouping — three buckets that mirror how marketers
// think about the customer journey (welcome new guests, keep the base
// loyal, win-back the ones slipping away). Each section has 2 cards.
// Unknown server-side keys fall into "Other" so a newly-added segment
// never silently disappears.
const SECTIONS: Array<{
  id: string
  label: string
  tagline: string
  keys: SegmentKey[]
}> = [
  {
    id: 'acquisition',
    label: 'Acquisition & Moments',
    tagline: 'Welcome new guests and mark special days.',
    keys: ['first_timers_recent', 'birthdays_this_month'],
  },
  {
    id: 'retention',
    label: 'Retention',
    tagline: 'Your base. Treat them right.',
    keys: ['vips', 'regulars'],
  },
  {
    id: 'winback',
    label: 'Win-back',
    tagline: 'At risk of slipping away — earn them back.',
    keys: ['at_risk', 'lapsed_60d'],
  },
]

/**
 * Segments tab — six static "saved query" tiles surfacing high-value
 * audiences (first-timers, birthdays, VIPs, regulars, at-risk, lapsed).
 *
 * Cards are grouped into three lifecycle sections so the marketer reads
 * the page as a story (Acquisition → Retention → Win-back) instead of
 * a flat grid. Each card shows the live member count, an optional
 * trend chip vs. 30 days ago, and Preview / CSV actions.
 *
 * Future: the Campaigns tab will hook into these segments for outbound
 * messaging — the "Send via campaign" action will appear on each card.
 */
export function AdminCustomerSegments() {
  const segmentsQuery = useQuery<APIResponse<CustomerSegmentSummary[]>>({
    queryKey: ['customer-segments'],
    queryFn: () => apiClient.listCustomerSegments(),
    staleTime: 60_000,
  })

  const [openKey, setOpenKey] = useState<SegmentKey | null>(null)

  const segments = segmentsQuery.data?.data ?? []

  // Bucket segments by lifecycle section. Anything not claimed by the
  // SECTIONS map falls into a final "Other" bucket — keeps the layout
  // resilient if a new server-side segment ships before the frontend
  // knows where to put it.
  const known = new Set(SECTIONS.flatMap((s) => s.keys))
  const sectionBuckets = SECTIONS.map((section) => ({
    ...section,
    items: section.keys
      .map((key) => segments.find((s) => s.key === key))
      .filter((s): s is CustomerSegmentSummary => Boolean(s)),
  }))
  const other = segments.filter((s) => !known.has(s.key))

  if (segmentsQuery.isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (segments.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
        No segments available.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {sectionBuckets.map((section) =>
        section.items.length === 0 ? null : (
          <SegmentSection key={section.id} {...section} onPreview={setOpenKey} />
        ),
      )}

      {other.length > 0 && (
        <SegmentSection
          id="other"
          label="Other"
          tagline=""
          items={other}
          onPreview={setOpenKey}
        />
      )}

      <SegmentPreviewDialog
        segmentKey={openKey}
        open={openKey !== null}
        onOpenChange={(open) => !open && setOpenKey(null)}
      />
    </div>
  )
}

function SegmentSection({
  label,
  tagline,
  items,
  onPreview,
}: {
  id: string
  label: string
  tagline: string
  keys?: SegmentKey[]
  items: CustomerSegmentSummary[]
  onPreview: (key: SegmentKey) => void
}) {
  return (
    <section className="space-y-2">
      {/* Header packed onto a single tight row: eyebrow label · tagline.
          Keeps the section divider scannable without burning two text
          rows of vertical space. */}
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </h2>
        {tagline && (
          <p className="text-sm text-muted-foreground/70">· {tagline}</p>
        )}
      </div>
      {/* 2-col on tablet+: each section has 2 cards, so this fills the
          row edge-to-edge with no empty slots. */}
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((seg) => (
          <SegmentTile key={seg.key} summary={seg} onPreview={() => onPreview(seg.key)} />
        ))}
      </div>
    </section>
  )
}

function SegmentTile({
  summary,
  onPreview,
}: {
  summary: CustomerSegmentSummary
  onPreview: () => void
}) {
  const { toast } = useToast()
  const presentation = SEGMENT_PRESENTATION[summary.key] || FALLBACK_PRESENTATION
  const Icon = presentation.icon
  const empty = summary.count === 0

  const exportMutation = useMutation({
    mutationFn: () => apiClient.downloadCustomerSegmentCSV(summary.key),
    onSuccess: (blob) => {
      const filename = `${summary.key}_${new Date().toISOString().slice(0, 10)}.csv`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      toast({
        title: 'CSV downloaded',
        description: `${filename} — ${summary.count.toLocaleString()} customers`,
      })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Export failed', description: msg, variant: 'destructive' })
    },
  })

  return (
    <div className={`rounded-xl border shadow-sm ${presentation.tone}`}>
      <div className="flex flex-col gap-2.5 p-4">
        {/* Header — tinted icon tile + segment label on the same row as
            the big count, so the card stays vertically compact. */}
        <div className="flex items-start gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${presentation.iconTone}`}
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold leading-tight text-foreground">
              {summary.label}
            </h3>
            <div className="mt-0.5 flex items-baseline gap-2">
              <div className="text-2xl font-extrabold leading-none tabular-nums text-foreground">
                {summary.count.toLocaleString()}
              </div>
              {empty ? (
                <span className="text-sm text-muted-foreground">
                  no customers
                </span>
              ) : (
                <TrendChip count={summary.count} prev={summary.count_30d_ago ?? null} />
              )}
            </div>
          </div>
        </div>

        {/* Tagline — context for what membership actually means. */}
        <p className="text-sm leading-snug text-muted-foreground line-clamp-2">
          {summary.tagline}
        </p>

        {/* Footer actions — Preview is the primary verb (90% of intent),
            CSV demoted to a ghost icon-only button. */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onPreview}
            disabled={empty}
            className="h-7 flex-1 gap-1.5 text-sm"
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => exportMutation.mutate()}
            disabled={empty || exportMutation.isPending}
            className="h-7 w-7 p-0"
            aria-label="Download CSV"
            title="Download CSV"
          >
            {exportMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** TrendChip — small pill showing change vs 30 days ago. Neutral colour
 *  regardless of direction (an increase in "Lapsed" is bad; an increase
 *  in "VIPs" is good — too risky to encode per-segment polarity here,
 *  let the reader interpret). Hidden when no prior count is available. */
function TrendChip({ count, prev }: { count: number; prev: number | null }) {
  if (prev === null || prev === undefined) return null
  const delta = count - prev
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground">
        <Minus className="h-3 w-3" aria-hidden />
        no change vs 30d
      </span>
    )
  }
  const positive = delta > 0
  const Arrow = positive ? ArrowUpRight : ArrowDownRight
  const sign = positive ? '+' : ''
  return (
    <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground">
      <Arrow className="h-3 w-3" aria-hidden />
      {sign}
      {delta.toLocaleString()} vs 30d
    </span>
  )
}

function SegmentPreviewDialog({
  segmentKey,
  open,
  onOpenChange,
}: {
  segmentKey: SegmentKey | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const segmentQuery = useQuery<APIResponse<CustomerSegmentDetail>>({
    queryKey: ['customer-segment', segmentKey, 'preview'],
    queryFn: () => apiClient.getCustomerSegment(segmentKey as SegmentKey, 200),
    enabled: Boolean(segmentKey) && open,
    staleTime: 60_000,
  })
  const detail = segmentQuery.data?.data

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="border-b border-border/70 px-6 py-4">
          <DialogTitle className="text-lg">
            {detail?.label || 'Segment preview'}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {detail?.tagline || ''}
            {detail ? (
              <>
                {' · '}
                <span className="font-semibold tabular-nums">
                  {detail.items.length.toLocaleString()}
                </span>{' '}
                of <span className="font-semibold tabular-nums">{detail.count.toLocaleString()}</span> shown
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto">
          {segmentQuery.isLoading || !detail ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : detail.items.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
              No customers match this segment yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="hidden md:table-cell">Email</TableHead>
                  <TableHead className="hidden sm:table-cell">Last visit</TableHead>
                  <TableHead className="text-right">Visits</TableHead>
                  <TableHead className="text-right">Lifetime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.display_name || (
                        <span className="text-muted-foreground italic">unnamed</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {row.phone_normalized || row.phone || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden truncate text-sm md:table-cell">
                      {row.email || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="hidden text-sm sm:table-cell">
                      {row.last_visit_at
                        ? formatRelative(row.last_visit_at)
                        : <span className="text-muted-foreground">Never</span>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.visit_count}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.lifetime_spend)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
